import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import ImageUploader from '@/components/feature/ImageUploader';
import ThumbImage from '@/components/base/ThumbImage';
import Pagination from '@/components/base/Pagination';
import ConfirmModal from '@/components/base/ConfirmModal';

interface Case {
  id: number;
  title: string;
  couple: string;
  from_location: string;
  baby_count: string;
  cycle: string;
  method: string;
  tag: string;
  content: string;
  img_url: string;
  status: string;
  featured: boolean;
  sort_order: number;
  published_at: string;
  created_at: string;
}

const TAGS = ['单代孕', '三代试管', '试管+代孕'];

function ImagePicker({ currentUrl, onSelect }: { currentUrl: string; onSelect: (url: string) => void }) {
  return (
    <ImageUploader
      currentUrl={currentUrl}
      onChange={onSelect}
      category="cases"
      placeholder="点击上传案例图片或粘贴 URL（JPG/PNG/WebP，最大2MB）"
      previewHeight="h-36"
      maxWidth={400}
    />
  );
}

function CaseModal({ caseItem, onClose, onSaved }: {
  caseItem: Case | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !caseItem;
  const [form, setForm] = useState({
    title: caseItem?.title || '',
    couple: caseItem?.couple || '',
    from_location: caseItem?.from_location || '',
    baby_count: caseItem?.baby_count || '',
    cycle: caseItem?.cycle || '',
    method: caseItem?.method || '',
    tag: caseItem?.tag || TAGS[0],
    content: caseItem?.content || '',
    img_url: caseItem?.img_url || '',
    status: caseItem?.status || 'published',
    featured: caseItem?.featured ?? false,
    sort_order: caseItem?.sort_order ?? 5,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.title) { setErr('标题不能为空'); return; }
    setSaving(true);
    setErr('');
    const payload = { ...form, updated_at: new Date().toISOString() };
    if (isNew) {
      const { error } = await supabase.from('success_cases').insert({ ...payload, published_at: new Date().toISOString() });
      if (error) { setErr(error.message); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('success_cases').update(payload).eq('id', caseItem!.id);
      if (error) { setErr(error.message); setSaving(false); return; }
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <h2 className="font-bold text-[#1a3a5c]">{isNew ? '添加成功案例' : '编辑案例'}</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">案例标题 *</label>
              <input type="text" value={form.title} onChange={set('title')} placeholder="如：北京赵女士：37岁圆梦..." className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">夫妻称呼</label>
              <input type="text" value={form.couple} onChange={set('couple')} placeholder="如：赵女士 & 赵先生" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">来自</label>
              <input type="text" value={form.from_location} onChange={set('from_location')} placeholder="如：中国·北京" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">宝宝信息</label>
              <input type="text" value={form.baby_count} onChange={set('baby_count')} placeholder="如：1男宝 / 双胞胎（1男1女）" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">标签类型</label>
              <select value={form.tag} onChange={set('tag')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] cursor-pointer bg-white">
                {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">发布状态</label>
              <select value={form.status} onChange={set('status')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] cursor-pointer bg-white">
                <option value="published">已发布</option>
                <option value="draft">草稿</option>
              </select>
            </div>
          </div>

          <div className="bg-[#f8f5f0] rounded-xl px-4 py-3 space-y-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setForm((p) => ({ ...p, featured: !p.featured }))}
                className={`flex items-center gap-2 text-sm font-medium cursor-pointer transition-colors ${form.featured ? 'text-[#b8944f]' : 'text-gray-400'}`}
              >
                <i className={`${form.featured ? 'ri-star-fill' : 'ri-star-line'} text-lg`}></i>
                {form.featured ? '已设为首页推荐' : '设为首页推荐'}
              </button>
              <span className="text-xs text-gray-400 ml-auto">首页好孕案例优先展示推荐案例</span>
            </div>
            {form.featured && (
              <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
                <label className="text-xs font-medium text-[#1a3a5c] whitespace-nowrap">推荐权重</label>
                <select
                  value={form.sort_order}
                  onChange={(e) => setForm((p) => ({ ...p, sort_order: parseInt(e.target.value) }))}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#c9a96e] cursor-pointer bg-white"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <option key={n} value={n}>{n} — {n <= 3 ? '最优先' : n <= 6 ? '中等' : '较低'}</option>
                  ))}
                </select>
                <span className="text-xs text-gray-400">数字越小越靠前</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">案例图片（抱娃照片）</label>
            <ImagePicker
              currentUrl={form.img_url}
              onSelect={(url) => setForm((p) => ({ ...p, img_url: url }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">案例故事（文字）</label>
            <textarea value={form.content} onChange={set('content')} rows={5} placeholder="填写委托家庭的真实故事，感人的经历描述..." className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] resize-none" maxLength={500} />
            <p className="text-xs text-gray-400 text-right mt-1">{form.content.length}/500</p>
          </div>

          {err && <p className="text-red-500 text-xs">{err}</p>}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer hover:border-gray-300 whitespace-nowrap">取消</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? '保存中...' : '保存案例'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CasesAdminPage() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Case | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterTag, setFilterTag] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  // Batch
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; danger?: boolean; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const fetchCases = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('success_cases').select('*');
    if (filterTag) query = query.eq('tag', filterTag);
    if (showFeaturedOnly) query = query.eq('featured', true);
    query = query.order('sort_order', { ascending: true }).order('created_at', { ascending: false });
    const { data } = await query;
    let result = data ?? [];

    // Client-side search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) => {
        return (
          (c.title && c.title.toLowerCase().includes(q)) ||
          (c.couple && c.couple.toLowerCase().includes(q)) ||
          (c.from_location && c.from_location.toLowerCase().includes(q)) ||
          (c.tag && c.tag.toLowerCase().includes(q)) ||
          (c.content && c.content.toLowerCase().includes(q))
        );
      });
    }

    setCases(result);
    setSelectedIds([]);
    setCurrentPage(1);
    setLoading(false);
  }, [filterTag, showFeaturedOnly, searchQuery]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const totalPages = Math.max(1, Math.ceil(cases.length / pageSize));
  const paginated = cases.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleDelete = async (c: Case) => {
    if (c.status === 'draft') {
      setConfirmModal({
        open: true,
        title: '永久删除案例',
        message: '确认永久删除该案例？此操作不可恢复。',
        danger: true,
        onConfirm: async () => {
          await supabase.from('success_cases').delete().eq('id', c.id);
          setConfirmModal((p) => ({ ...p, open: false }));
          fetchCases();
        },
      });
    } else {
      setConfirmModal({
        open: true,
        title: '下架案例',
        message: '确认下架该案例？下架后可在草稿中找到。',
        onConfirm: async () => {
          await supabase.from('success_cases').update({ status: 'draft' }).eq('id', c.id);
          setConfirmModal((p) => ({ ...p, open: false }));
          fetchCases();
        },
      });
    }
  };

  const handleBatchUpdateStatus = async (newStatus: string) => {
    if (!selectedIds.length) return;
    const { error } = await supabase.from('success_cases').update({ status: newStatus }).in('id', selectedIds);
    if (error) {
      setToast({ msg: '批量更新失败', type: 'error' });
    } else {
      setToast({ msg: `成功更新 ${selectedIds.length} 条案例`, type: 'success' });
      setSelectedIds([]);
      fetchCases();
    }
  };

  const handleBatchDelete = async () => {
    if (!selectedIds.length) return;
    setConfirmModal({
      open: true,
      title: '批量删除案例',
      message: `确认批量删除选中的 ${selectedIds.length} 个案例？此操作不可恢复。`,
      danger: true,
      onConfirm: async () => {
        const { error } = await supabase.from('success_cases').delete().in('id', selectedIds);
        if (error) {
          setToast({ msg: '批量删除失败', type: 'error' });
        } else {
          setToast({ msg: `成功删除 ${selectedIds.length} 个案例`, type: 'success' });
          setSelectedIds([]);
          fetchCases();
        }
        setConfirmModal((p) => ({ ...p, open: false }));
      },
    });
  };

  const handleToggleFeatured = async (c: Case) => {
    await supabase.from('success_cases').update({ featured: !c.featured, sort_order: !c.featured ? 5 : null }).eq('id', c.id);
    fetchCases();
  };

  const openNew = () => { setEditing(null); setShowModal(true); };
  const openEdit = (c: Case) => { setEditing(c); setShowModal(true); };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">案例管理</h1>
          <p className="text-gray-400 text-sm mt-1">管理好孕成功案例，打通前端案例页面和首页轮播</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-1.5 bg-[#c9a96e] hover:bg-[#b8944f] text-white px-4 py-2 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>添加案例
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-2xl p-4 mb-5 flex flex-wrap gap-2 items-center">
        <button onClick={() => setFilterTag('')} className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${!filterTag ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>全部</button>
        {TAGS.map((t) => (
          <button key={t} onClick={() => setFilterTag(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${filterTag === t ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{t}</button>
        ))}
        <button
          onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${showFeaturedOnly ? 'bg-[#c9a96e] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
        >
          <i className={`text-xs ${showFeaturedOnly ? 'ri-star-fill' : 'ri-star-line'}`}></i>首页推荐
        </button>
        <div className="ml-auto text-xs text-gray-400 self-center">
          {showFeaturedOnly ? (
            <>筛选结果：{cases.length} 条</>
          ) : (
            <><i className="ri-star-fill text-[#c9a96e] mr-1"></i>{cases.filter(c => c.featured).length} 条首页推荐</>
          )}
        </div>
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white w-full sm:w-auto mt-2 sm:mt-0">
          <i className="ri-search-line text-gray-400 text-sm"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索标题 / 来源 / 标签..."
            className="bg-transparent text-sm text-gray-600 outline-none flex-1 min-w-[120px]"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-circle-fill text-sm"></i>
            </button>
          )}
        </div>
      </div>

      {/* Batch action bar */}
      {selectedIds.length > 0 && (
        <div className="bg-[#1a3a5c] rounded-2xl px-5 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-white text-sm font-medium">已选中 {selectedIds.length} 个案例</span>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => handleBatchUpdateStatus('published')} className="px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-check-line mr-1"></i>批量发布
            </button>
            <button onClick={() => handleBatchUpdateStatus('draft')} className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-eye-off-line mr-1"></i>批量下架
            </button>
            <button onClick={handleBatchDelete} className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-delete-bin-line mr-1"></i>批量删除
            </button>
            <button onClick={() => setSelectedIds([])} className="text-white/60 hover:text-white text-xs cursor-pointer ml-2">
              清除选择
            </button>
          </div>
        </div>
      )}

      {/* Cases grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-400"><i className="ri-loader-4-line text-2xl animate-spin block mb-2"></i>加载中...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {paginated.map((c) => (
              <div key={c.id} className={`bg-white rounded-2xl overflow-hidden border-2 transition-all relative ${c.featured ? 'border-[#c9a96e]/40' : 'border-transparent'} ${selectedIds.includes(c.id) ? 'ring-2 ring-[#c9a96e]' : ''}`}>
                {/* Checkbox */}
                <div className="absolute top-3 right-3 z-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    className="w-4 h-4 rounded border-gray-300 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="relative">
                  {c.img_url ? (
                    <div className="w-full h-44 overflow-hidden">
                      <ThumbImage src={c.img_url} alt={c.title} className="w-full h-full object-cover object-top" />
                    </div>
                  ) : (
                    <div className="w-full h-44 bg-[#f8f5f0] flex items-center justify-center text-gray-300">
                      <i className="ri-image-line text-3xl"></i>
                    </div>
                  )}
                  {c.featured && (
                    <div className="absolute top-2 left-2 bg-[#c9a96e] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-xs"></i>W{c.sort_order ?? 5}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-[#c9a96e]/10 text-[#b8944f] px-2 py-0.5 rounded-full">{c.tag}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'published' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {c.status === 'published' ? '已发布' : '草稿'}
                    </span>
                  </div>
                  <h3 className="font-bold text-[#1a3a5c] text-sm line-clamp-2 mb-1">{c.title}</h3>
                  <p className="text-gray-400 text-xs">{c.from_location} · {c.baby_count}</p>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleToggleFeatured(c)}
                      title={c.featured ? '取消首页推荐' : '设为首页推荐'}
                      className={`flex items-center gap-1 text-xs cursor-pointer transition-colors ${c.featured ? 'text-[#c9a96e] hover:text-gray-400' : 'text-gray-300 hover:text-[#c9a96e]'}`}
                    >
                      <i className={`${c.featured ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
                      {c.featured ? '推荐中' : '推荐'}
                    </button>
                    <button onClick={() => openEdit(c)} className="text-xs text-[#c9a96e] hover:underline cursor-pointer">编辑</button>
                    <button onClick={() => handleDelete(c)} className={`text-xs cursor-pointer whitespace-nowrap ${c.status === 'draft' ? 'text-red-400 hover:text-red-600' : 'text-gray-400 hover:text-red-500'}`}>
                      {c.status === 'draft' ? '删除' : '下架'}
                    </button>
                    <span className="ml-auto text-xs text-gray-300">{new Date(c.created_at).toLocaleDateString('zh-CN')}</span>
                  </div>
                </div>
              </div>
            ))}
            {cases.length === 0 && (
              <div className="col-span-3 text-center py-12 text-gray-400">
                <i className="ri-heart-line text-4xl mb-3 block"></i>暂无案例，点击上方按钮添加
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl mt-5">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={cases.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      )}

      {showModal && (
        <CaseModal
          caseItem={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchCases(); }}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        danger={confirmModal.danger}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((p) => ({ ...p, open: false }))}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-[60] px-5 py-3 rounded-xl shadow-lg text-sm font-medium transition-all">
          <div className={`${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'} px-4 py-3 rounded-xl flex items-center gap-2`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill text-green-500' : 'ri-error-warning-fill text-red-500'}`}></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}